import { DateFormat, TimeFormat } from '../types';
import { TFunction } from 'i18next';
import { formats } from '../constants';
import { formatOrdinalNumber, formatDisplayName, formatCurrency } from '../utils';
import { FormatDisplayNameOptions } from '@formatjs/intl';
import { DateInput, NumberWithConfigInput, TimeInput } from '../types';

/**
 * Formats a currency value with locale-appropriate formatting.
 * @param value - The numeric value to format
 * @param t - i18next translation function
 * @param currencyCode - Optional ISO 4217 currency code (e.g., 'USD', 'EUR', 'CAD').
 *                       If provided, uses the specific currency; otherwise uses the default.
 * @returns Formatted currency string with the correct symbol and locale formatting
 */
export function localizeCurrency(value: string | number, t: TFunction, currencyCode?: string): string {
  if (currencyCode) {
    return formatCurrency(value, currencyCode);
  }
  return t('formats.currency', {
    value,
    defaultValue: '{{ value, currency }}'
  });
}

export function localizeDate(value: DateInput, t: TFunction, type: DateFormat = formats.dateShort): string {
  return t(`formats.${type}`, {
    value,
    defaultValue: `{{ value, ${type} }}`
  });
}

export function localizeDateTime(
  value: string | Date,
  t: TFunction,
  dateType: DateFormat = formats.dateShort,
  timeType: TimeFormat = formats.timeMedium
): string {
  const date = t(`formats.${dateType}`, {
    value,
    defaultValue: `{{ value, ${dateType} }}`
  });
  const time = t(`formats.${timeType}`, {
    value: {
      time: value
    },
    defaultValue: `{{ value, ${timeType} }}`
  });

  return `${date} ${time}`;
}

export function localizeDistance(value: string | number, t: TFunction): string {
  return t('formats.distance', {
    value,
    defaultValue: '{{ value, distance }}'
  });
}

export function localizeNumber(value: string | number | NumberWithConfigInput, t: TFunction): string {
  return t('formats.number', {
    value,
    defaultValue: '{{ value, number }}'
  });
}

export function localizeRelativeTime(
  value: string | number | Date,
  t: TFunction,
  type: string = formats.relativeLong
): string {
  return t(`formats.${type}`, {
    value,
    defaultValue: `{{ value, ${type} }}`
  });
}

export function localizeTime(
  value: string | Date | TimeInput,
  t: TFunction,
  type: TimeFormat = formats.timeShort
): string {
  return t(`formats.${type}`, {
    value,
    defaultValue: `{{ value, ${type} }}`
  });
}

export const stringFormat = (input: string, ...params: string[]): string =>
  input.replace(/{(\d+)}/g, (match, number) => params[number] ?? match);

export function localizeOrdinalNumber(value: string | number, t: TFunction) {
  return formatOrdinalNumber(value, t);
}

export function localizeDisplayName(value: string, options?: FormatDisplayNameOptions & { customLocale?: string }) {
  return formatDisplayName(value, options);
}
